<script setup lang="ts">
const { data: page } = await useAsyncData('index', () => queryCollection('index').first())

const title = computed(() => page.value?.seo?.title || page.value?.title || 'Velara Hosting')
const description = computed(() => page.value?.seo?.description || page.value?.description || '')

useSeoMeta({
  titleTemplate: '',
  title: () => title.value,
  ogTitle: () => title.value,
  description: () => description.value,
  ogDescription: () => description.value
})
</script>

<template>
  <div v-if="page">
    <!-- Section 1: Top Hero -->
    <UPageHero
      v-if="page"
      :title="page.title"
      :description="page.description"
      :links="page.hero?.links"
      class="relative pt-0!"
    >
      <template #top>
        <div class="absolute inset-0 -z-10 bg-[url('https://cdn.velarahosting.com/HomeBackground.png')] bg-cover bg-center opacity-60" />
      </template>

      <template #title>
        <div class="w-190.5 text-center justify-center">
          <span class="text-White-1---100 text-4xl font-normal font-['Supercell-Magic'] uppercase leading-16 [text-shadow:0px_2px_6px_rgb(0_0_0/0.06)]">
            Minecraft Server
          </span>
          <span class="text-Green-01---100 text-4xl font-normal font-['Supercell-Magic'] uppercase leading-16 [text-shadow:0px_2px_6px_rgb(0_0_0/0.06)]">
            Hosting Evolved
          </span>
        </div>
      </template>

      <PromotionalVideo />
    </UPageHero>

    <!-- Section 2: Trusted by Players -->
    <UPageSection
      v-if="(page as any).trusted_by"
      :title="(page as any).trusted_by.title"
      :description="(page as any).trusted_by.description"
      class="relative"
    >
      <template #top>
        <div class="absolute inset-0 -z-10 bg-[url('https://cdn.velarahosting.com/Minecraft-BG.png')] bg-cover bg-center opacity-50" />
      </template>
      <TrustedByPlayers
        :testimonials="(page as any).trusted_by.items"
        :video-poster="(page as any).trusted_by.videoPoster"
        :video-src="(page as any).trusted_by.videoSrc"
      />
    </UPageSection>

    <!-- Section 3: Easy. Fast. Powerful -->
    <UPageSection
      v-if="page.features_grid"
      :title="page.features_grid.title"
      :description="page.features_grid.description"
      class="relative"
    >
      <template #top>
        <div class="absolute inset-0 -z-10 bg-[url('https://cdn.velarahosting.com/HomeBackground.png')] bg-cover bg-center opacity-50" />
      </template>
      <FeatureBreakdown :items="page.features_grid.items" />
    </UPageSection>

    <!-- Section 4: Hosting Plans -->
    <UPageSection
      v-if="page.hosting_plans"
      id="plans"
      :title="page.hosting_plans.title"
      :description="page.hosting_plans.description"
      class="relative"
    >
      <template #top>
        <div class="absolute inset-0 -z-10 bg-[url('https://cdn.velarahosting.com/Hytale-01.png')] bg-cover bg-center opacity-50" />
      </template>
      <UPricingPlans v-if="page.hosting_plans.items">
        <PricingCard
          v-for="(plan, index) in page.hosting_plans.items"
          :key="index"
          v-bind="plan"
          :image="plan.image || 'https://placehold.co/101x101'"
          :plan-name="plan.title"
          :price="plan.price"
          :features="plan.features"
          :banner-text="plan.bannerText"
          :color="plan.color || '#B91C1C'"
          :background-image="plan.backgroundImage || 'https://picsum.photos/400/500?random=' + index"
        />
      </UPricingPlans>
    </UPageSection>

    <!-- Section 5: Server Calculator -->
    <UPageSection
      id="calculator"
      title="Not sure what to pick?"
      description="Use our server calculator to find the best plan for your needs."
      class="relative"
    >
      <template #top>
        <div class="absolute inset-0 -z-10 bg-[url('https://cdn.velarahosting.com/BG-OP20-Top.png')] bg-cover bg-center opacity-50" />
      </template>
      <ServerCalculator />
    </UPageSection>

    <!-- Section 6: Why players pick us -->
    <UPageSection
      v-if="page.why_pick_us"
      id="comparison"
      :title="page.why_pick_us.title"
      :description="page.why_pick_us.description"
      class="relative"
    >
      <template #top>
        <div class="absolute inset-0 -z-10 bg-[url('https://picsum.photos/1920/1080?random=6')] bg-cover bg-center opacity-10" />
      </template>
      <div class="max-w-5xl mx-auto">
        <ComparisonTable
          :columns="page.why_pick_us.columns"
          :rows="page.why_pick_us.rows"
        />
        <ComparisonCTA
          v-if="page.why_pick_us.cta"
          v-bind="page.why_pick_us.cta"
        />
      </div>
    </UPageSection>

    <!-- Section 7: FAQ -->
    <UPageSection
      v-if="page.faq"
      id="faq"
      class="relative"
    >
      <template #top>
        <div class="absolute inset-0 -z-10 bg-[url('https://picsum.photos/1920/1080?random=7')] bg-cover bg-center opacity-10" />
      </template>

      <div class="grid lg:grid-cols-2 gap-12 items-start">
        <div class="space-y-6">
          <h2 class="text-4xl md:text-5xl font-black font-display tracking-tight uppercase leading-tight">
            {{ page.faq.title }}
          </h2>
          <div class="text-lg text-gray-400 whitespace-pre-line leading-relaxed">
            {{ page.faq.description }}
          </div>
          <UButton
            v-if="page.faq.button"
            v-bind="page.faq.button"
            class="mt-4"
          />
        </div>

        <div v-if="page.faq.items">
          <FaqItem
            v-for="(item, index) in page.faq.items"
            :key="index"
            v-bind="item"
          />
        </div>
      </div>
    </UPageSection>

    <!-- Section 8: Loved by players worldwide -->
    <UPageSection
      v-if="page.loved_by"
      id="loved-by"
      :title="page.loved_by.title"
      :description="page.loved_by.description"
      class="relative"
    >
      <template #top>
        <div class="absolute inset-0 -z-10 bg-[url('https://picsum.photos/1920/1080?random=8')] bg-cover bg-center opacity-10" />
      </template>
      <UPageColumns
        v-if="page.loved_by.items"
        class="xl:columns-2"
      >
        <UPageCard
          v-for="(testimonial, index) in page.loved_by.items"
          :key="index"
          variant="subtle"
          :description="testimonial.quote"
        >
          <template #footer>
            <UUser
              v-bind="testimonial.user"
              size="lg"
            />
          </template>
        </UPageCard>
      </UPageColumns>
    </UPageSection>

    <!-- Section Ending: Join Discord -->
    <UContainer class="my-20">
      <DiscordCTA
        v-if="page.join_discord"
        :title="page.join_discord.title"
        :description="page.join_discord.description"
        :button-label="page.join_discord.links?.[0]?.label"
        :to="page.join_discord.links?.[0]?.to"
      />
    </UContainer>
  </div>
</template>
